import { motion } from "framer-motion";
import {
  Leaf,
  MapPin,
  Shield,
  Sparkles,
} from "lucide-react";

export function CoreValues() {
  const values = [
    {
      icon: Leaf,
      title: "Sustainability",
      description:
        "We view waste as a renewable carbon resource and convert it into sustainable fuel feedstock with measurable GHG reduction.",
      color: "from-emerald-500 via-green-500 to-teal-600",
      iconBg: "from-emerald-500 to-green-600",
      borderColor: "border-green-500/30",
    },
    {
      icon: MapPin,
      title: "100% Traceability",
      description:
        "Every drop of feedstock is digitally mapped to its origin, logistics, quality, and carbon pathway using IoT-backed trace layers.",
      color: "from-blue-500 via-cyan-500 to-sky-600",
      iconBg: "from-blue-500 to-cyan-600",
      borderColor: "border-blue-500/30",
    },
    {
      icon: Shield,
      title: "Integrity & Compliance",
      description:
        "We operate with enforceable documentation, regulatory alignment, and sustainability certifications for audit-ready operations.",
      color: "from-indigo-500 via-purple-500 to-violet-600",
      iconBg: "from-indigo-500 to-purple-600",
      borderColor: "border-indigo-500/30",
    },
  ];

  return (
    <section className="py-32 bg-gradient-to-br from-slate-50 via-white to-gray-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%237CB342' fill-opacity='1'%3E%3Cpath d='M0 0h40v40H0V0zm40 40h40v40H40V40zm0-40h2l-2 2V0zm0 4l4-4h2l-6 6V4zm0 4l8-8h2L40 10V8zm0 4L52 0h2L40 14v-2zm0 4L56 0h2L40 18v-2zm0 4L60 0h2L40 22v-2zm0 4L64 0h2L40 26v-2zm0 4L68 0h2L40 30v-2zm0 4L72 0h2L40 34v-2zm0 4L76 0h2L40 38v-2zm0 4L80 0v2L42 40h-2zm4 0L80 4v2L46 40h-2zm4 0L80 8v2L50 40h-2zm4 0l28-28v2L54 40h-2zm4 0l24-24v2L58 40h-2zm4 0l20-20v2L62 40h-2zm4 0l16-16v2L66 40h-2zm4 0l12-12v2L70 40h-2zm4 0l8-8v2l-6 6h-2zm4 0l4-4v2l-2 2h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Decorative Blobs */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-[#7CB342]/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-gradient-to-br from-[#1976D2]/10 to-transparent rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border border-[#7CB342]/20 rounded-full px-6 py-3 mb-6"
          >
            <Sparkles className="w-5 h-5 text-[#7CB342]" />
            <span className="text-gray-900 font-semibold text-sm">What We Stand For</span>
          </motion.div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Core Values
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Our principles guide every decision we make and every partnership we build
          </p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              {/* Animated Gradient Border */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${value.color} rounded-3xl opacity-30 group-hover:opacity-75 blur-sm group-hover:blur transition-all duration-500`} />
              
              {/* Card Content */}
              <div className="relative bg-white rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 h-full">
                {/* Icon */}
                <div className="mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${value.iconBg} rounded-2xl shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-4 transition-colors duration-300">
                  {value.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-sm">
                  {value.description}
                </p>

                {/* Decorative Element */}
                <div className={`absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-br ${value.color} opacity-5 rounded-tl-full`} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative"
        >
          {/* Gradient Border */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] via-[#1976D2] to-[#7CB342] rounded-3xl opacity-50 blur-sm" />
          
          {/* Content */}
          <div className="relative bg-white rounded-3xl p-10 lg:p-16 text-center shadow-2xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-full mb-8 shadow-xl">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              
              <p className="text-xl lg:text-2xl text-gray-800 max-w-5xl mx-auto leading-relaxed">
                These values aren't just words — they're{" "}
                <span className="font-bold text-[#7CB342]">embedded</span> in our
                technology, <span className="font-bold text-[#1976D2]">operations</span>,
                and every partnership we forge.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}